import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    grecaptcha?: any;
    mvRecaptchaReady?: () => void;
  }
}

/**
 * The reCAPTCHA site key. MyVaidyam's key is registered for myvaidyam.com and
 * www.myvaidyam.com only, so everywhere else (staging, local) the box uses
 * Google's public test key, which always passes and is labelled "for testing
 * purposes only". VITE_RECAPTCHA_SITE_KEY overrides both at build time. The
 * live mailer (/api/inquiry) checks each token with the matching secret key,
 * which lives only in the mailer's .env on the live server. The Geo pages
 * (scripts/geo/template-us.html) make the same choice.
 */
const LIVE_SITE_KEY = "6Ldq6u4tAAAAAO89hMpie3iqkFMGCJ2k3IH3xf7c";
const TEST_SITE_KEY = "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI";
const onLiveDomain = typeof location !== "undefined" && /(^|\.)myvaidyam\.com$/.test(location.hostname);
// (optional: the prerender bundles this for Node, where import.meta.env is absent)
export const RECAPTCHA_SITE_KEY: string =
  import.meta.env?.VITE_RECAPTCHA_SITE_KEY || (onLiveDomain ? LIVE_SITE_KEY : TEST_SITE_KEY);

/* Google's script is fetched once, the first time a form with the box is shown. */
let loading: Promise<void> | null = null;
function loadRecaptcha(): Promise<void> {
  return (loading ??= new Promise((resolve) => {
    if (window.grecaptcha?.render) return resolve();
    window.mvRecaptchaReady = () => resolve();
    const s = document.createElement("script");
    s.src = "https://www.google.com/recaptcha/api.js?onload=mvRecaptchaReady&render=explicit";
    s.async = true;
    s.defer = true;
    document.head.appendChild(s);
  }));
}

export type RecaptchaHandle = { reset: () => void };

/* Google draws the box at a fixed 304 x 78 */
const WIDGET_W = 304;
const WIDGET_H = 78;

/**
 * The "I'm not a robot" box; reports its token (empty when it expires).
 *
 * Google's box is a fixed 304px wide. On a narrow phone (a 320px screen, or a
 * larger one zoomed) the form has less room than that, and the box pushed
 * the whole form wider than the dialog, which then slid sideways. So the box
 * is shrunk to the width it is given, and kept out of the form's width.
 */
const Recaptcha = forwardRef<RecaptchaHandle, { onToken: (token: string) => void }>(({ onToken }, ref) => {
  const box = useRef<HTMLDivElement>(null);
  const frame = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const widget = useRef<number | null>(null);
  const report = useRef(onToken);
  report.current = onToken;

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widget.current !== null) window.grecaptcha?.reset(widget.current);
      report.current("");
    },
  }));

  useEffect(() => {
    let gone = false;
    loadRecaptcha().then(() => {
      if (gone || !box.current || widget.current !== null) return;
      widget.current = window.grecaptcha.render(box.current, {
        sitekey: RECAPTCHA_SITE_KEY,
        callback: (t: string) => report.current(t),
        "expired-callback": () => report.current(""),
        "error-callback": () => report.current(""),
      });
    });
    return () => {
      gone = true;
    };
  }, []);

  // shrink the box to the room it has, never above its own size
  useEffect(() => {
    const el = frame.current;
    if (!el) return;
    const fit = () => setScale(Math.min(1, el.clientWidth / WIDGET_W) || 1);
    fit();
    const watch = new ResizeObserver(fit);
    watch.observe(el);
    return () => watch.disconnect();
  }, []);

  // the box is placed absolutely, so its 304px never widens the form
  return (
    <div ref={frame} className="relative w-full" style={{ height: WIDGET_H * scale }}>
      <div
        ref={box}
        className="absolute left-0 top-0"
        style={{ width: WIDGET_W, transform: scale < 1 ? `scale(${scale})` : undefined, transformOrigin: "left top" }}
      />
    </div>
  );
});
Recaptcha.displayName = "Recaptcha";

export default Recaptcha;
